/*
  Sticky add to cart (theme blocks).

  The bar never adds to cart on its own: the button clicks the PDP form's submit button, so
  selling plans, quantity breaks, free gifts and the cart drawer all behave exactly like the
  main button. Variant, quantity and price state are read from and written to the form through
  the Elixir_* helpers in product-form-controller.js.
*/
(function () {
  if (customElements.get('sticky-atc-bar')) return;

  var BREAKPOINT = 750;

  function formExists() {
    return typeof Elixir_ProductFormExists === 'function' && Elixir_ProductFormExists();
  }

  function getForm() {
    return formExists() ? Elixir_GetProductForm() : null;
  }

  function getMainButton() {
    var form = getForm();
    return form ? form.querySelector('button[type="submit"][name="add"]') : null;
  }

  function formatMoney(cents) {
    if (typeof window.Elixir_FormatMoney === 'function') return window.Elixir_FormatMoney(cents);
    return '$' + (Math.round(cents) / 100).toFixed(2);
  }

  function formQuantity() {
    var form = getForm();
    if (!form) return 1;
    var paid = 0;
    form.querySelectorAll('input[name^="items["][name$="][quantity]"]').forEach(function (input) {
      var match = input.name.match(/^items\[(\d+)\]\[quantity\]$/);
      if (match && form.querySelector('input[name="items[' + match[1] + '][properties][_free_item]"]')) return;
      if (input.closest('.upsell-bulk-items-container')) return;
      paid += parseInt(input.value, 10) || 0;
    });
    if (paid > 0) return paid;
    var qty = form.querySelector('input[name="quantity"]');
    if (!qty && form.id) qty = document.querySelector('input[name="quantity"][form="' + form.id + '"]');
    return qty ? Math.max(1, parseInt(qty.value, 10) || 1) : 1;
  }

  function addBusinessDays(date, days, skipWeekends) {
    var result = new Date(date.getTime());
    var added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      var day = result.getDay();
      if (skipWeekends && (day === 0 || day === 6)) continue;
      added++;
    }
    if (skipWeekends) {
      while (result.getDay() === 0 || result.getDay() === 6) result.setDate(result.getDate() + 1);
    }
    return result;
  }

  function formatDate(date, format) {
    var locale = document.documentElement.lang || 'en-US';
    var options;
    if (format === 'short') options = { weekday: 'short', month: 'short', day: 'numeric' };
    else if (format === 'month_day') options = { month: 'long', day: 'numeric' };
    else if (format === 'numeric') options = { month: 'numeric', day: 'numeric' };
    else options = { weekday: 'long', month: 'long', day: 'numeric' };
    try {
      return date.toLocaleDateString(locale, options).replace(',', '');
    } catch (e) {
      return date.toDateString();
    }
  }

  class StickyAtcBar extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._cleanup = [];
      this._state = { targetVisible: false, targetAbove: false, drawerOpen: false, scrolled: false };
      this.trigger = this.dataset.trigger || 'after_atc';
      this.variants = this.readJson('[data-satc-variants]') || [];

      this.bindDates();
      this.bindButtons();
      this.bindVariantSelects();
      this.bindQuantity();
      this.bindPrice();
      this.setupDrawerWatch();
      this.setupVisibility();
      this.syncFromForm();

      if (window.Shopify && window.Shopify.designMode && this.dataset.editorPreview === 'true') {
        this.forceVisible = true;
        this.render();
      }
    }

    disconnectedCallback() {
      this._connected = false;
      this._cleanup.forEach(function (fn) { fn(); });
      this._cleanup = [];
      document.documentElement.classList.remove('satc-visible');
    }

    on(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this._cleanup.push(function () { target.removeEventListener(type, handler, options); });
    }

    readJson(selector) {
      var el = this.querySelector(selector);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    currentVariant() {
      var id = formExists() ? Elixir_GetProductFormVariantId() : null;
      for (var i = 0; i < this.variants.length; i++) {
        if (String(this.variants[i].id) === String(id)) return this.variants[i];
      }
      return this.variants[0] || null;
    }

    /* ── Visibility ─────────────────────────────────────────── */

    setupVisibility() {
      var self = this;
      var target = getMainButton() || document.querySelector(this.dataset.anchor || '.pdp-atc-block-root');

      if (target && 'IntersectionObserver' in window && (this.trigger === 'after_atc' || this.trigger === 'always_except_atc')) {
        var observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            self._state.targetVisible = entry.isIntersecting;
            self._state.targetAbove = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
          });
          self.render();
        });
        observer.observe(target);
        this._cleanup.push(function () { observer.disconnect(); });
      } else if (this.trigger !== 'always') {
        this.trigger = 'scroll';
      }

      var onScroll = function () {
        var max = document.documentElement.scrollHeight - window.innerHeight;
        var pct = parseFloat(self.dataset.scrollPercent) || 10;
        self._state.scrolled = max > 0 && (window.scrollY / max) * 100 >= pct;
        if (self.trigger === 'scroll') self.render();
      };
      this.on(window, 'scroll', onScroll, { passive: true });
      onScroll();

      var resize = function () { self.updateHeightVar(); };
      this.on(window, 'resize', resize, { passive: true });
      this.render();
    }

    setupDrawerWatch() {
      var self = this;
      var drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      var check = function () {
        self._state.drawerOpen = drawer.classList.contains('active');
        self.render();
      };
      var observer = new MutationObserver(check);
      observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      this._cleanup.push(function () { observer.disconnect(); });
      check();
    }

    shouldShow() {
      if (this.forceVisible) return true;
      if (this._state.drawerOpen) return false;
      switch (this.trigger) {
        case 'always':
          return true;
        case 'always_except_atc':
          return !this._state.targetVisible;
        case 'scroll':
          return this._state.scrolled;
        default:
          return this._state.targetAbove;
      }
    }

    render() {
      var visible = this.shouldShow();
      if (visible === this._visible) return;
      this._visible = visible;
      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (visible) this.removeAttribute('inert');
      else this.setAttribute('inert', '');
      document.documentElement.classList.toggle('satc-visible', visible && this.dataset.position !== 'top');
      this.updateHeightVar();
    }

    updateHeightVar() {
      var inner = this.querySelector('.satc__bar');
      if (inner) document.documentElement.style.setProperty('--satc-height', inner.offsetHeight + 'px');
    }

    /* ── Button ─────────────────────────────────────────────── */

    bindButtons() {
      var self = this;
      this.buttons = Array.prototype.slice.call(this.querySelectorAll('[data-satc-button]'));
      this.buttons.forEach(function (button) {
        self.on(button, 'click', function (event) {
          var behavior = button.getAttribute('data-behavior');
          if (behavior === 'link') return;
          event.preventDefault();
          if (behavior === 'scroll') return self.scrollToBuyBox();
          var main = getMainButton();
          if (!main) return self.scrollToBuyBox();
          if (main.disabled || main.getAttribute('aria-disabled') === 'true') return;
          main.click();
        });
      });

      var main = getMainButton();
      if (!main) return;
      var mirror = function () {
        var loading = main.classList.contains('loading');
        var soldOut = main.disabled && !loading;
        self.buttons.forEach(function (button) {
          if (button.getAttribute('data-behavior') !== 'add') return;
          button.classList.toggle('is-loading', loading);
          button.classList.toggle('is-sold-out', soldOut);
          button.toggleAttribute('disabled', soldOut);
          var label = button.querySelector('[data-satc-button-label]');
          if (label) {
            if (!label.dataset.defaultText) label.dataset.defaultText = label.textContent;
            label.textContent = soldOut ? (button.getAttribute('data-sold-out-text') || label.dataset.defaultText) : label.dataset.defaultText;
          }
        });
      };
      var observer = new MutationObserver(mirror);
      observer.observe(main, { attributes: true, attributeFilter: ['class', 'disabled', 'aria-disabled'] });
      this._cleanup.push(function () { observer.disconnect(); });
      mirror();
    }

    scrollToBuyBox() {
      var form = getForm();
      var target = (form && (form.closest('.shopify-section') || form)) || document.querySelector(this.dataset.anchor || '.pdp-atc-block-root');
      if (!target) return;
      var offset = parseInt(this.dataset.scrollOffset, 10) || 0;
      var top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    /* ── Variants ───────────────────────────────────────────── */

    bindVariantSelects() {
      var self = this;
      this.selects = Array.prototype.slice.call(this.querySelectorAll('[data-satc-option]'));
      this.selects.forEach(function (select) {
        self.on(select, 'change', function () {
          var position = select.getAttribute('data-satc-option');
          self.selects.forEach(function (other) {
            if (other !== select && other.getAttribute('data-satc-option') === position) other.value = select.value;
          });
          self.applySelectedOptions(position, select.value);
        });
      });

      this.on(document, 'simple-variant-picker:variantChange', function (event) {
        var variant = event.detail && event.detail.variant;
        self.syncFromVariant(variant || self.currentVariant());
      });
      this.on(document, 'variant:change', function () { self.syncFromForm(); });
      var form = getForm();
      if (form) {
        this.on(form, 'change', function (event) {
          if (event.target && event.target.name === 'id') self.syncFromForm();
        });
      }
    }

    applySelectedOptions(position, value) {
      var chosen = [];
      var self = this;
      var positions = {};
      this.selects.forEach(function (select) { positions[select.getAttribute('data-satc-option')] = select.value; });
      var current = this.currentVariant();
      var optionCount = current && current.options ? current.options.length : 0;
      for (var i = 1; i <= optionCount; i++) chosen.push(positions[i] != null ? positions[i] : current.options[i - 1]);

      var match = this.variants.find(function (variant) {
        return variant.options.every(function (opt, idx) { return opt === chosen[idx]; });
      });
      if (!match) return;

      var picker = document.querySelector('.simple-variant-picker__radio[data-option-position="' + position + '"][value="' + String(value).replace(/"/g, '\\"') + '"]');
      if (picker && !picker.disabled) {
        picker.checked = true;
        picker.dispatchEvent(new Event('change', { bubbles: true }));
      }
      var pickerSelect = document.querySelector('.simple-variant-picker__select[data-option-position="' + position + '"]');
      if (pickerSelect && pickerSelect.value !== value) {
        pickerSelect.value = value;
        pickerSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
      var card = document.querySelector('.variant-product-card[data-variant-id="' + match.id + '"]');
      if (card && !card.classList.contains('selected')) card.click();

      if (formExists()) Elixir_SetProductFormVariantId(match.id);
      self.syncFromVariant(match);
    }

    syncFromForm() {
      this.syncFromVariant(this.currentVariant());
    }

    syncFromVariant(variant) {
      if (!variant) return;
      (this.selects || []).forEach(function (select) {
        var position = parseInt(select.getAttribute('data-satc-option'), 10);
        var value = variant.options && variant.options[position - 1];
        if (value != null && select.value !== value) select.value = value;
      });
      this.querySelectorAll('[data-satc-image][data-source="variant"]').forEach(function (img) {
        var media = variant.featured_image || variant.featured_media && variant.featured_media.preview_image;
        var src = media && (media.src || media.url);
        if (!src) return;
        var width = img.getAttribute('data-width') || '160';
        var next = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
        if (img.getAttribute('src') !== next) {
          img.setAttribute('src', next);
          img.removeAttribute('srcset');
        }
      });
      this.schedulePrice();
    }

    /* ── Quantity ───────────────────────────────────────────── */

    bindQuantity() {
      var self = this;
      this.qtyInputs = Array.prototype.slice.call(this.querySelectorAll('[data-satc-qty-input]'));
      if (!this.qtyInputs.length) return;
      var setQty = function (qty) {
        qty = Math.max(1, parseInt(qty, 10) || 1);
        self.qtyInputs.forEach(function (input) { input.value = qty; });
        if (formExists() && typeof Elixir_SetProductFormQuantity === 'function') {
          Elixir_SetProductFormQuantity(qty, { source: 'sticky-atc' });
        }
      };
      this.querySelectorAll('[data-satc-qty-step]').forEach(function (button) {
        self.on(button, 'click', function () {
          var current = parseInt(self.qtyInputs[0].value, 10) || 1;
          setQty(current + parseInt(button.getAttribute('data-satc-qty-step'), 10));
        });
      });
      this.qtyInputs.forEach(function (input) {
        self.on(input, 'change', function () { setQty(input.value); });
      });
      this.on(document, 'quantity:changed', function (event) {
        var qty = event.detail && event.detail.quantity;
        if (qty) self.qtyInputs.forEach(function (input) { input.value = qty; });
      });
      this.qtyInputs.forEach(function (input) { input.value = formQuantity(); });
    }

    /* ── Price ──────────────────────────────────────────────── */

    bindPrice() {
      var self = this;
      this.priceEls = Array.prototype.slice.call(this.querySelectorAll('[data-satc-price]'));
      this.buttonPriceEls = Array.prototype.slice.call(this.querySelectorAll('[data-satc-button-price]'));
      if (!this.priceEls.length && !this.buttonPriceEls.length) return;
      var schedule = function () { self.schedulePrice(); };
      ['quantity:changed', 'quantity_break:changed', 'subscriptionContext:updated', 'elixir-pricing-ready', 'elixir:price-override-changed'].forEach(function (type) {
        self.on(document, type, schedule);
      });
      var form = getForm();
      if (form) this.on(form, 'change', schedule);

      var main = getMainButton();
      var mainPrice = main && main.querySelector('[data-elixir-atc-price]');
      if (mainPrice) {
        var observer = new MutationObserver(schedule);
        observer.observe(mainPrice, { childList: true, subtree: true, characterData: true });
        this._cleanup.push(function () { observer.disconnect(); });
      }
      this.schedulePrice();
    }

    schedulePrice() {
      if (!this.priceEls || (!this.priceEls.length && !this.buttonPriceEls.length)) return;
      var self = this;
      cancelAnimationFrame(this._priceFrame);
      this._priceFrame = requestAnimationFrame(function () { self.updatePrice(); });
    }

    /* Prefer the main ATC button's price (price-sync.js already accounts for quantity breaks,
       bundles and subscriptions). Fall back to variant price x form quantity. */
    resolvePrice() {
      var main = getMainButton();
      var current = main && main.querySelector('[data-elixir-atc-price] .current-price');
      if (current && current.textContent.trim()) {
        var compareEl = main.querySelector('[data-elixir-atc-price] .compare-price');
        return {
          price: current.textContent.replace(/^[\s•·-]+/, '').trim(),
          compare: compareEl ? compareEl.textContent.replace(/[()]/g, '').trim() : ''
        };
      }
      var override = window.Elixir_PriceOverride ? window.Elixir_PriceOverride.get() : null;
      if (override && override.price > 0) {
        return {
          price: formatMoney(override.price),
          compare: override.compareAtPrice > override.price ? formatMoney(override.compareAtPrice) : ''
        };
      }
      var variant = this.currentVariant();
      if (!variant) return null;
      var qty = formQuantity();
      var result = { price: variant.price * qty, compareAtPrice: (variant.compare_at_price || 0) * qty };
      if (typeof window.Elixir_GetDisplayPrice === 'function') {
        result = window.Elixir_GetDisplayPrice({
          price: variant.price,
          compareAtPrice: variant.compare_at_price || 0,
          quantity: qty,
          variantId: variant.id,
          applySubscriptionDiscount: true
        });
      }
      var compare = result.compareAtPrice > result.price ? result.compareAtPrice : 0;
      if (window.subscriptionContext && !compare && variant.price * qty > result.price) compare = variant.price * qty;
      return { price: formatMoney(result.price), compare: compare ? formatMoney(compare) : '' };
    }

    updatePrice() {
      var resolved = this.resolvePrice();
      if (!resolved) return;
      this.priceEls.concat(this.buttonPriceEls).forEach(function (el) {
        var priceEl = el.querySelector('[data-satc-price-current]');
        var compareEl = el.querySelector('[data-satc-price-compare]');
        if (priceEl && priceEl.textContent !== resolved.price) priceEl.textContent = resolved.price;
        if (compareEl) {
          var show = el.getAttribute('data-show-compare') === 'true' && resolved.compare && resolved.compare !== resolved.price;
          compareEl.textContent = show ? resolved.compare : '';
          compareEl.hidden = !show;
        }
      });
    }

    /* ── Delivery dates ─────────────────────────────────────── */

    bindDates() {
      var now = new Date();
      this.querySelectorAll('[data-satc-date]').forEach(function (el) {
        var days = parseInt(el.getAttribute('data-days'), 10) || 0;
        var cutoff = parseInt(el.getAttribute('data-cutoff-hour'), 10);
        if (!isNaN(cutoff) && cutoff > 0 && now.getHours() >= cutoff) days += 1;
        var skip = el.getAttribute('data-skip-weekends') === 'true';
        el.textContent = formatDate(addBusinessDays(now, days, skip), el.getAttribute('data-format'));
      });
    }
  }

  customElements.define('sticky-atc-bar', StickyAtcBar);

  document.addEventListener('shopify:block:select', function (event) {
    var bar = event.target && event.target.closest && event.target.closest('sticky-atc-bar');
    if (bar) {
      bar.forceVisible = true;
      bar.render();
    }
  });
})();
